/**
 * Cross-tab pivot helpers.
 *
 * When a user clicks an entity (ASN, email, business, JA4, IP, country,
 * session) anywhere in the dashboard, we want the destination tab to
 * show ONLY that entity's events — not that entity AND any leftover
 * narrow filter from a previous lookup. These helpers clear all the
 * narrow filter keys, then set the one we care about, then switch tab,
 * all in a single URL update so the destination renders with the
 * correct filter on first paint.
 */
import type { useUrlState } from "@/hooks/useUrlState";

type PatchUrl = ReturnType<typeof useUrlState>[1];

/** All narrow per-entity filter keys the pivot wipes on every jump. */
const NARROW_KEYS = [
  "asn", "ip", "ja4", "ja3", "email", "businessId", "sessionId",
  "country", "colo", "endpointGroups", "endpointType", "pathPrefix",
  "method", "riskBands", "scoreMin", "scoreMax", "managedList",
  "detectionId", "reasonContains", "userAgentContains", "action",
  "outcome", "botScoreBucket", "threatScoreMin", "threatScoreMax",
  "verifiedBot",
] as const;

type PivotTarget = "investigations";

/**
 * Build a URL patch that clears every narrow filter, sets only the
 * caller's keys, and switches tab. `timeRange` and `customFromIso` /
 * `customToIso` are intentionally NOT cleared — the user's chosen
 * window still applies.
 */
export function pivotPatch(
  set: Record<string, string | undefined>,
  tab: PivotTarget,
): Record<string, string | undefined> {
  const patch: Record<string, string | undefined> = {};
  for (const k of NARROW_KEYS) patch[k] = undefined;
  for (const [k, v] of Object.entries(set)) patch[k] = v;
  patch.tab = tab;
  return patch;
}

export function pivotTo(
  patchUrl: PatchUrl,
  set: Record<string, string | undefined>,
  tab: PivotTarget,
) {
  patchUrl(pivotPatch(set, tab));
}
